import { useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { evidenceFor, type EvidenceItem } from '../../store/selectors'
import { formatFriendlyDate, formatRelative } from '../../lib/dates'

/**
 * The observations behind a level.
 *
 * This is what makes the number trustworthy. Every level in this app is an
 * inference, and an inference a parent cannot audit is just an opinion with a
 * decimal point on it. So: the date, what it said, what it showed, and her own
 * words where there are any — and the raw note one tap away, because the
 * summary is itself an inference.
 *
 * Observations that only touch the same area of work are shown too, but under
 * their own heading. Passing them off as direct evidence would be the exact
 * dishonesty this component exists to prevent.
 */
export function EvidenceTrail({
  domainKey,
  skillKey,
  skillName,
  childName,
  initialLimit = 4,
}: {
  domainKey?: string
  skillKey?: string
  skillName?: string
  childName?: string
  initialLimit?: number
}) {
  const items = useAuroraStore((s: any) => evidenceFor(s, domainKey, skillKey))
  const [expanded, setExpanded] = useState(false)

  const direct = items.filter((e: EvidenceItem) => e.direct)
  const nearby = items.filter((e: EvidenceItem) => !e.direct)
  const shown = expanded ? items : items.slice(0, initialLimit)
  const shownDirect = shown.filter((e: EvidenceItem) => e.direct)
  const shownNearby = shown.filter((e: EvidenceItem) => !e.direct)

  if (items.length === 0) {
    return (
      <section className="evidence">
        <h3 className="section-title">What this is based on</h3>
        <div className="evidence-empty">
          <p className="evidence-empty-body">
            Nothing logged against {skillName ? `“${skillName}”` : 'this'} yet, so treat any
            number here as a starting guess rather than a finding.
          </p>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => (auroraStore.getState() as any).startRecording()}
          >
            <MicIcon /> Log a moment
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="evidence">
      <div className="section-head">
        <h3 className="section-title">What this is based on</h3>
        <span className="evidence-count tnum">
          {direct.length > 0
            ? `${direct.length} observation${direct.length === 1 ? '' : 's'}`
            : `${nearby.length} nearby`}
        </span>
      </div>

      {shownDirect.length > 0 && (
        <ol className="evidence-list">
          {shownDirect.map((item: EvidenceItem) => (
            <EvidenceRow key={item.captureId} item={item} childName={childName} />
          ))}
        </ol>
      )}

      {shownNearby.length > 0 && (
        <>
          <p className="evidence-aside">
            {direct.length > 0 ? 'Also around this time' : 'Nothing names this skill directly'}.
            These were logged in the same area of work:
          </p>
          <ol className="evidence-list is-nearby">
            {shownNearby.map((item: EvidenceItem) => (
              <EvidenceRow key={item.captureId} item={item} childName={childName} />
            ))}
          </ol>
        </>
      )}

      {items.length > initialLimit && (
        <button
          className="btn btn-quiet btn-sm evidence-more"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : `Show all ${items.length}`}
        </button>
      )}
    </section>
  )
}

function EvidenceRow({ item, childName }: { item: EvidenceItem; childName?: string }) {
  const [showRaw, setShowRaw] = useState(false)
  const summary = item.summary || item.text || 'Logged without a note.'
  const hasRaw = !!item.text && item.text.trim() !== item.summary?.trim()

  return (
    <li className="evidence-item">
      <div className="evidence-when">
        <time dateTime={new Date(item.at).toISOString()} className="evidence-date">
          {formatFriendlyDate(new Date(item.at).toISOString().slice(0, 10))}
        </time>
        <span className="evidence-ago">{formatRelative(item.at)}</span>
      </div>

      <p className="evidence-summary">{summary}</p>

      {(item.signal || item.suggestedLevel || item.affect) && (
        <div className="evidence-signals">
          {item.signal && (
            <span className={`signal signal-${item.signal}`}>{signalLabel(item.signal)}</span>
          )}
          {typeof item.suggestedLevel === 'number' && (
            <span className="signal signal-read tnum">Read as level {item.suggestedLevel}</span>
          )}
          {item.affect && <span className="evidence-affect">{affectLabel(item.affect, childName)}</span>}
        </div>
      )}

      {item.quote && <blockquote className="evidence-quote">“{item.quote}”</blockquote>}

      {hasRaw && (
        <>
          <button
            className="evidence-raw-toggle"
            onClick={() => setShowRaw((v) => !v)}
            aria-expanded={showRaw}
          >
            {showRaw ? 'Hide what you logged' : 'What you logged'}
          </button>
          {showRaw && <p className="evidence-raw">{item.text}</p>}
        </>
      )}
    </li>
  )
}

/**
 * The four signals, in a parent's words. "Refused" stays factual rather than
 * disapproving — a child declining an activity is information about the
 * activity at least as often as it is about the child.
 */
const SIGNALS: Record<string, string> = {
  demonstrated: 'Did it',
  partial: 'Partly',
  struggled: 'Found it hard',
  refused: 'Not today',
}
function signalLabel(signal: string): string {
  return SIGNALS[signal] ?? signal
}

const AFFECTS: Record<string, string> = {
  delighted: 'loved it',
  engaged: 'was into it',
  neutral: 'was even about it',
  frustrated: 'got frustrated',
  refused: 'was done with it',
}
function affectLabel(affect: string, childName?: string): string {
  const words = AFFECTS[affect]
  if (!words) return affect
  return `${childName ?? 'She'} ${words}`
}

const MicIcon = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v4" />
  </svg>
)
